import debounce from 'lodash/debounce';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import axios from 'axios';
import domReady from '../helpers/dom-ready';

class ExhibitionSearch {
  static init() {
    return new ExhibitionSearch();
  }

  constructor() {
    const exhibitionHeader = document.querySelector('.exhibition-header');
    if (!exhibitionHeader) { return; }

    this.openButton = exhibitionHeader.querySelector('.open-search-button');
    this.closeButton = exhibitionHeader.querySelector('.close-search-button');
    this.wrapper = exhibitionHeader.querySelector('.search');
    this.searchInput = exhibitionHeader.querySelector('#search-input');

    this.addEventListeners();
    this.getData();
  }

  addEventListeners() {
    this.openButton.addEventListener('click', (e) => { this.onClickOpenButton(e); });
    this.closeButton.addEventListener('click', (e) => { this.onClickCloseButton(e); });

    this.lastFocusedElement = null;
    window.addEventListener('keyup', (e) => { this.onKeyup(e); });
  }

  onKeyup(e) {
    if (e.key === 'Escape') {
      if (document.activeElement.classList.contains('search-result-link')) {
        this.searchInput.focus();
      } else {
        this.onClickCloseButton();
      }
    }

    // Keep navigating around in the search view whilst its open
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (document.activeElement === this.openButton) {
          this.searchInput.focus();
        }
      } else {
        if (this.lastFocusedElement === this.closeButton) {
          e.preventDefault();
          this.searchInput.focus();
          this.lastFocusedElement = null;
        }

        if (document.activeElement === this.closeButton) {
          this.lastFocusedElement = this.closeButton;
        }
      }
    }

    // Delete last character and put focus in search field when user has selected suggested item
    if (e.key === 'Backspace') {
      if (document.activeElement.classList.contains('search-result-link')) {
        this.searchInput.value = this.searchInput.value.slice(0, -1);
        this.searchInput.focus();
      }
    }
  }

  onClickOpenButton() {
    this.wrapper.classList.remove('-hidden');
    this.wrapper.ariaHidden = false;

    disableBodyScroll(this.wrapper);

    this.searchInput.focus();
    this.searchInput.selectionStart = 0;
    this.searchInput.selectionEnd = this.searchInput.value.length;
  }

  onClickCloseButton() {
    this.wrapper.classList.add('-hidden');
    this.wrapper.ariaHidden = true;

    enableBodyScroll(this.wrapper);
  }

  getData() {
    this.apiEndpoint = this.wrapper.dataset.apiUrl;

    if (this.apiEndpoint) {
      axios.get(this.apiEndpoint)
        .then((response) => {
          this.objectsData = response.data;
          this.initializeSearch();
        });
    }
  }

  initializeSearch() {
    this.searchInput = document.querySelector('#search-input');
    this.searchResults = document.querySelector('#search-results');
    this.searchInstructions = document.querySelector('#search-instruction');

    if (this.searchInput) {
      // Event listener for input changes
      this.searchInput.addEventListener('input', debounce(() => { this.doSearch(); }, 200));

      // Show results again when clicking into/focusing the search field
      this.searchInput.addEventListener('focus', () => { this.doSearch(); });

      // Handle keyboard navigation and selection
      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const firstResult = this.searchResults.querySelector('li > a');
          if (firstResult) {
            firstResult.focus();
          }
        }
      });

      this.searchResults.addEventListener('keydown', (e) => {
        const results = this.searchResults.querySelectorAll('li > a');
        const focusedIndex = Array.from(results).indexOf(document.activeElement);

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const nextIndex = focusedIndex < results.length - 1 ? focusedIndex + 1 : 0;
          results[nextIndex].focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          const prevIndex = focusedIndex > 0 ? focusedIndex - 1 : results.length - 1;
          results[prevIndex].focus();
        }
      });
    }
  }

  doSearch() {
    const searchText = this.searchInput.value.toLowerCase();

    // Clear previous results
    this.searchResults.innerHTML = '';

    if (searchText.length === 0) {
      this.searchInstructions.classList.remove('-hidden');
      this.searchInstructions.textContent = this.searchInstructions.dataset.instructionsText;
    }

    if (searchText.length > 0 && searchText.length < 3) {
      this.searchInstructions.classList.remove('-hidden');
      this.searchInstructions.textContent = this.searchInstructions.dataset.minimumCharText;
    }

    if (searchText.length >= 3) {
      this.searchInstructions.classList.add('-hidden');
    }

    // Check if the input meets the minimum character requirement (e.g., 2 characters)
    if (searchText.length >= 3) {
      const matchingItems = this.objectsData.filter((item) => {
        const titleMatch = item.title
          ? item.title.toLowerCase().includes(searchText)
          : null;
        const numberMatch = item.objectNumber
          ? item.objectNumber.toString().toLowerCase().includes(searchText)
          : null;
        return titleMatch || numberMatch;
      });

      if (matchingItems.length > 0) {
        // Display matching items as links
        matchingItems.forEach((item) => {
          const listItem = document.createElement('li');

          const link = document.createElement('a');
          link.href = item.permalink;
          link.classList.add('search-result-link');

          const title = document.createElement('span');
          title.classList.add('title');
          title.textContent = item.title;

          const objectNumber = document.createElement('span');
          objectNumber.classList.add('object-number');
          objectNumber.textContent = item.objectNumber;

          link.appendChild(title);
          link.appendChild(objectNumber);

          listItem.appendChild(link);
          this.searchResults.appendChild(listItem);
        });
      } else {
        this.searchInstructions.classList.remove('-hidden');
        this.searchInstructions.textContent = this.searchInstructions.dataset.noMatchText;
      }
    } else if (searchText.length !== 0) {
      this.searchInstructions.classList.add('-visible');
    }
  }

  clearSearchResult(focusInput = false) {
    this.searchResults.innerHTML = '';
    if (focusInput) {
      this.searchInput.focus();
    }
  }
}

domReady(() => {
  ExhibitionSearch.init();
});
